import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useSocket } from "../chatWindow/contexts/SocketContext";
import AddDetails from "./AddDetails";
import Followup from "./FollowUp";
import StateApi from "../../api/StateApi";
import LeadFinalApi from "../../api/leadFinalApi";
import FollowupApi from "../../api/followUpApi";
import { DISPOSITION_COLORS } from "../../utils/dispositionColors";
import { ucfirst } from "../../helpers/helper";
import ProposalApi from "../../api/proposalApi";
import axios from "../../api/axiosinstance";
import { useNavigate } from "react-router-dom";
import Auth from "../../utils/AuthUtils";
import { can } from "../../utils/permissionUtils";
import { useParams, Link, Navigate, useLocation } from "react-router-dom";

function LeadDetails() {
  const params = useParams();
  const { id } = params;
  const socket = useSocket();

  const [lead, setLead] = useState(null);
  const [followUp, setFollowups] = useState([]);
  const [error, setError] = useState(null);
  const [sendemailData, setSendEmailData] = useState(null);
  const [followupsData, setFollowupsData] = useState(null);
  const [stateName, setStateName] = useState("");
  const [districtName, setDistrictName] = useState("");
  const [proposals, setProposals] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [documents, setDocuments] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [journey, setJourney] = useState(null);

  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [meetings, setMeetings] = useState([]);

  const [newPrice, setNewPrice] = useState("");
  // ✅ Get User Permissions
  const user = Auth.getUserDetails();
  const permissions = user?.permissions;

  const [developers, setDevelopers] = useState([]);

  const [projectForm, setProjectForm] = useState({
    projectName: "",
    assignedDeveloper: "",
    priority: "medium",
    deadline: "",
    notes: "",
  });

  /* ================= 🛡️ RBAC GUARD ================= */
  const hasReadAccess =
    user?.role === "superadmin" || can(permissions, "read", "lead");

  useEffect(() => {
    if (!id) return;

    fetchLead();
    fetchProposals();
    fetchAgreements();
    fetchDocuments(); // ✅ ADD THIS
  }, [id]);

  useEffect(() => {
    if (!socket) return;

    const handleJourneyUpdate = (data) => {
      console.log("🚀 JOURNEY UPDATE:", data);

      if (data?.leadId === id) {
        fetchJourney();
        fetchDocuments();
        fetchAgreements();
        fetchFollowups();
      }
    };

    socket.off("journey_update");

    socket.on("journey_update", handleJourneyUpdate);

    return () => {
      socket.off("journey_update", handleJourneyUpdate);
    };
  }, [socket, id]);

  const fetchLead = async () => {
    try {
      const res = await LeadFinalApi.details(id);
      if (res.code === 200) {
        setLead(res.data);
        fetchFollowups();
      }
    } catch {
      setError("Failed to load lead");
    }
  };

  const fetchMeetings = async () => {
    try {
      const res = await axios.get(`/meetings/lead/${id}`);

      if (res.code === 200) {
        setMeetings(res.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, [id]);

  const fetchAgreements = async () => {
    try {
      const res = await axios.get(`/service-agreement/by-lead/${id}`);

      console.log("🔥 AGREEMENTS:", res);

      if (res?.code === 200) {
        setAgreements(res.data || []);
      } else {
        setAgreements([]);
      }
    } catch (err) {
      console.error("Agreement fetch error:", err);

      setAgreements([]);
    }
  };
  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`/documents/by-lead/${id}`);

      console.log("🔥 DOCUMENT API:", res);

      if (res.code === 200) {
        setDocuments(res.data);
      }
    } catch (err) {
      console.error("Document error:", err);
    }
  };

  const fetchProposals = async () => {
    if (!id) return;

    try {
      const res = await ProposalApi.getByLead(id); // ❌ lead._id nahi
      if (res.code === 200) {
        setProposals(res.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFollowups = async () => {
    try {
      const res = await FollowupApi.list(id);
      if (res.code === 200) setFollowups(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchJourney = async () => {
    try {
      const res = await axios.get(`/client-journey/by-lead/${id}`);

      console.log("🔥 JOURNEY API RESPONSE:", res);

      if (res?.success) {
        setJourney(res.data);
      }
    } catch (err) {
      console.log("Journey Error:", err);
    }
  };

  const fetchDevelopers = async () => {
    try {
      const res = await axios.get("/users/list");

      if (res.code === 200) {
        const devs = res.data.filter(
          (u) => u.roleId?.role?.toLowerCase() === "developer",
        );

        setDevelopers(devs);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const openProjectModal = () => {
    const modal = new window.bootstrap.Modal(
      document.getElementById("projectModal"),
    );

    modal.show();
  };

  const handleCreateProject = async () => {
    try {
      const payload = {
        leadId: lead._id,

        projectName: projectForm.projectName,

        assignedDeveloper: projectForm.assignedDeveloper,

        priority: projectForm.priority,

        deadline: projectForm.deadline,

        notes: projectForm.notes,
      };

      const res = await axios.post("/projects/create", payload);

      if (res.code === 200) {
        alert("Project Created");

        const modalEl = document.getElementById("projectModal");

        const modal = window.bootstrap.Modal.getInstance(modalEl);

        modal.hide();
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchJourney();
  }, [id]);

  useEffect(() => {
    fetchDevelopers();
  }, []);

  /* ================= RESOLVE STATE & DISTRICT ================= */
  useEffect(() => {
    const resolveLocationNames = async () => {
      if (!lead?.state) return;
      try {
        const stateRes = await StateApi.getStates();
        if (stateRes.code === 200) {
          const state = stateRes.data.find((s) => s._id === lead.state);
          setStateName(state?.name || "");
        }
        const districtRes = await StateApi.getDistrictsByState(lead.state);
        if (districtRes.code === 200) {
          const district = districtRes.data.find(
            (d) => d._id === lead.district,
          );
          setDistrictName(district?.name || "");
        }
      } catch (err) {
        console.error("Failed to resolve location names", err);
      }
    };
    // resolveLocationNames();
  }, [lead]);

  const handleDownload = async (id, proposalNumber) => {
    try {
      const response = await ProposalApi.download(id);

      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${proposalNumber}.pdf`;
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  const handleDocumentDownload = async (id, documentNumber) => {
    try {
      const response = await axios.get(`/documents/download/${id}`, {
        responseType: "blob",
      });

      console.log("PDF RESPONSE:", response);

      // ✅ FIXED
      const blob = new Blob([response], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      // ✅ OPEN PDF
      window.open(url, "_blank");

      // ✅ OPTIONAL DOWNLOAD
      const link = document.createElement("a");

      link.href = url;

      link.download = `${documentNumber}.pdf`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (err) {
      console.error("Document download failed", err);
    }
  };

  const openPriceModal = (item) => {
    setSelectedFollowup(item);

    setNewPrice(item.price || "");

    const modal = new window.bootstrap.Modal(
      document.getElementById("priceModal"),
    );

    modal.show();
  };

  const handleUpdatePrice = async () => {
    try {
      await axios.put(`/followUps/update-price/${selectedFollowup._id}`, {
        price: newPrice,
      });

      fetchFollowups();

      fetchDocuments();

      const modalEl = document.getElementById("priceModal");

      const modal = window.bootstrap.Modal.getInstance(modalEl);

      modal.hide();
    } catch (err) {
      console.log(err);
    }
  };

  const handleLeadTemperature = async (e) => {
    try {
      const value = e.target.value;

      const res = await LeadFinalApi.update({
        id: lead._id,
        leadTemperature: value,
      });

      if (res.code === 200) {
        setLead((prev) => ({
          ...prev,
          leadTemperature: value,
        }));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleAgreementDownload = async (id, agreementNumber) => {
    try {
      const response = await axios.get(`/agreements/download/${id}`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${agreementNumber}.pdf`;
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Agreement download failed", err);
    }
  };

  // ✅ Access Denied Check
  // if (!hasReadAccess) return <Navigate to="/403" replace />;

  if (error) return <div className="m-4 text-danger">{error}</div>;

  if (!lead) return <div className="m-4">Loading...</div>;

  return (
    <div className="content">
      <div className="container-fluid py-4">
        <Link
          to={`/lead-management${location.search}`}
          className="text-primary mb-3 d-inline-block"
        >
          ← Back to Leads
        </Link>

        {/* ================= HEADER ================= */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-body d-flex align-items-center">
            <div className="avatar avatar-lg rounded-circle bg-info text-white me-3 d-flex align-items-center justify-content-center">
              <i className="ti ti-user fs-24"></i>
            </div>
            <div>
              <h4 className="mb-0">{lead.name}</h4>
              <small className="text-muted">{lead.company_name || "—"}</small>
            </div>

            {/* <button onClick={handleCreateAgreement}>
  ➕ Create Agreement
</button> */}

            <div className="ms-auto">
              <button
                className="btn btn-warning me-2"
                onClick={() => navigate(`/lead-edit/${lead._id}`)}
              >
                <i className="ti ti-edit"></i>
                Edit Lead
              </button>

              {can(permissions, "write", "email") && (
                <button
                  className="btn btn-outline-primary"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvas_add"
                  onClick={() => setSendEmailData(lead)}
                >
                  <i className="ti ti-mail" /> Send Email
                </button>
              )}

              <div className="mt-3">
                <label className="form-label fw-bold">Lead Temperature</label>

                <select
                  className="form-select"
                  value={lead?.leadTemperature || "Cold"}
                  onChange={handleLeadTemperature}
                >
                  <option value="Hot">🔥 Hot Lead</option>
                  <option value="Warm">🟡 Warm Lead</option>
                  <option value="Cold">🔵 Cold Lead</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          {/* ================= LEFT COLUMN ================= */}
          <div className="col-md-4">
            {/* <div className="lead-glass-card">
              <div className="card-body">
                <h6 className="fw-bold mb-3">Lead Information</h6>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/chat?leadId=${lead._id}`)}
                >
                  <i className="ti ti-message-circle"></i>
                </button>
                <p>
                  <strong>Lead Id:</strong> {lead.leadId}
                </p>
                <p>
                  <strong>Date Created:</strong>{" "}
                  {new Date(lead.createdAt).toLocaleString()}
                </p>
              </div>
            </div> */}

            {/* <div className="card mb-3 border-0 shadow-sm">
              <div className="card-body">
                <h6 className="fw-bold mb-3">Meta Information</h6>
                {Object.entries(lead.meta || {}).map(([key, value]) => (
                  <p key={key}>
                    <strong>{key.replace(/_/g, " ")}:</strong>{" "}
                    {value !== null && value !== undefined && value !== ""
                      ? value
                      : "—"}
                  </p>
                ))}
              </div>
            </div> */}

            <div className="lead-glass-card lead-info-card">
              <div className="lead-card-top">
                <div className="lead-card-icon red">
                  <i className="ti ti-user-circle"></i>
                </div>

                <div>
                  <h4 className="lead-card-title">Lead Information</h4>

                  <p className="lead-card-subtitle">
                    Basic lead details & activity
                  </p>
                </div>
              </div>

              <div className="lead-card-content">
                <div className="lead-detail-row">
                  <span>Lead ID</span>
                  <strong>{lead.leadId}</strong>
                </div>

                <div className="lead-detail-row">
                  <span>Created Date</span>

                  <strong>{new Date(lead.createdAt).toLocaleString()}</strong>
                </div>

                <button
                  className="lead-chat-btn"
                  onClick={() => navigate(`/chat?leadId=${lead._id}`)}
                >
                  <i className="ti ti-message-circle"></i>
                  Open Conversation
                </button>
              </div>
            </div>
            <div className="lead-glass-card">
              <div className="lead-card-top">
                <div className="lead-card-icon purple">
                  <i className="ti ti-database"></i>
                </div>

                <div>
                  <h4 className="lead-card-title">Meta Information</h4>

                  <p className="lead-card-subtitle">
                    Client requirements & metadata
                  </p>
                </div>
              </div>

              <div className="lead-grid-info">
                <div className="lead-detail-row">
                  <span>Platform</span>

                  <strong>
                    <span
                      className={`badge ${
                        lead?.meta?.platform === "axesell.in"
                          ? "bg-primary"
                          : lead?.meta?.platform === "axesell.com"
                            ? "bg-success"
                            : "bg-secondary"
                      }`}
                    >
                      {lead?.meta?.platform || "Unknown"}
                    </span>
                  </strong>
                </div>
              </div>

              <div className="lead-grid-info">
                {Object.entries(lead.meta || {}).map(([key, value]) => (
                  <div className="lead-grid-box" key={key}>
                    <label>{key.replace(/_/g, " ")}</label>

                    <span>
                      {value !== null && value !== undefined && value !== ""
                        ? value
                        : "—"}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* <div className="card mb-3 border-0 shadow-sm">
              <div className="card-body">
                <h6 className="fw-bold mb-3">Company Information</h6>
                <p>
                  <strong>Name:</strong> {lead.company_name || "—"}
                </p>
                <p>
                  <strong>Type:</strong> {lead.companyType || "—"}
                </p>
                <p>
                  <strong>Designation:</strong> {lead.designation || "—"}
                </p>
              </div>
            </div> */}

            <div className="lead-glass-card">
              <div className="lead-card-top">
                <div className="lead-card-icon blue">
                  <i className="ti ti-building-skyscraper"></i>
                </div>

                <div>
                  <h4 className="lead-card-title">Company Information</h4>

                  <p className="lead-card-subtitle">
                    Business & designation details
                  </p>
                </div>
              </div>

              <div className="lead-grid-info">
                <div className="lead-grid-box">
                  <label>Company Name</label>
                  <span>{lead.company_name || "—"}</span>
                </div>

                <div className="lead-grid-box">
                  <label>Company Type</label>
                  <span>{lead.companyType || "—"}</span>
                </div>

                <div className="lead-grid-box">
                  <label>Designation</label>
                  <span>{lead.designation || "—"}</span>
                </div>
              </div>
            </div>

            <div className="lead-glass-card">
              <div className="lead-card-top">
                <div className="lead-card-icon green">
                  <i className="ti ti-phone"></i>
                </div>

                <div>
                  <h4 className="lead-card-title">Contact</h4>

                  <p className="lead-card-subtitle">
                    Client communication details
                  </p>
                </div>
              </div>

              <div className="contact-card">
                <div className="contact-item">
                  <div className="contact-icon mail">
                    <i className="ti ti-mail"></i>
                  </div>

                  <div>
                    <div className="contact-label">Email</div>

                    <div className="contact-value">{lead.email || "—"}</div>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon phone">
                    <i className="ti ti-phone"></i>
                  </div>

                  <div>
                    <div className="contact-label">Phone</div>

                    <div className="contact-value">{lead.phone || "—"}</div>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon whatsapp">
                    <i className="ti ti-brand-whatsapp"></i>
                  </div>

                  <div>
                    <div className="contact-label">WhatsApp</div>

                    <div className="contact-value">
                      {lead.whatsapp_number || "—"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* <div className="card mb-3 border-0 shadow-sm">
              <div className="card-body">
                <h6 className="fw-bold mb-3">Contact</h6>
                <p>
                  <i className="ti ti-mail me-2 text-primary" />
                  {lead.email || "—"}
                </p>
                <p>
                  <i className="ti ti-phone me-2 text-primary" />
                  {lead.phone || "—"}
                </p>
                <p>
                  <i className="ti ti-brand-whatsapp me-2 text-success" />
                  {lead.whatsapp_number || "—"}
                </p>
              </div>
            </div> */}

            <div className="lead-glass-card doc-history-card">
              {/* HEADER */}
              <div className="lead-card-top">
                <div className="lead-card-icon red">
                  <i className="ti ti-file-text"></i>
                </div>

                <div>
                  <h4 className="lead-card-title">Document History</h4>

                  <p className="lead-card-subtitle">
                    Track client document activity
                  </p>
                </div>

                {documents.length > 0 && (
                  <div className="doc-count-badge">{documents.length}</div>
                )}
              </div>

              {/* LIST */}
              {documents.length > 0 ? (
                <div className="doc-history-list">
                  {documents.map((d, index) => (
                    <div
                      key={d._id}
                      className="doc-history-item"
                      style={{
                        animationDelay: `${index * 0.08}s`,
                      }}
                    >
                      {/* TOP */}
                      <div className="doc-top">
                        <div className="doc-left">
                          <div className="doc-icon">
                            <i className="ti ti-file-description"></i>
                          </div>

                          <div>
                            <div className="doc-number">{d.documentNumber}</div>

                            <div className="doc-date">
                              {new Date(d.createdAt).toLocaleString()}
                            </div>
                          </div>
                        </div>

                        <button
                          className="doc-download-btn"
                          onClick={() =>
                            handleDocumentDownload(d._id, d.documentNumber)
                          }
                        >
                          <i className="ti ti-download"></i>
                        </button>
                      </div>

                      {/* STATS */}
                      <div className="doc-stats-grid">
                        {/* OPEN COUNT */}
                        <div className="doc-stat-box">
                          <div className="doc-stat-label">Open Count</div>

                          <div className="doc-stat-value blue">
                            👁️ {d.openCount || 0}
                          </div>
                        </div>

                        {/* LAST OPENED */}
                        <div className="doc-stat-box">
                          <div className="doc-stat-label">Last Opened</div>

                          <div className="doc-stat-time">
                            {d.lastOpenedAt
                              ? new Date(d.lastOpenedAt).toLocaleString()
                              : "Not Opened Yet"}
                          </div>
                        </div>
                      </div>

                      {/* STATUS */}
                      <div className="doc-status-row">
                        <div
                          className={`doc-status-badge ${
                            d.openCount > 0 ? "viewed" : "pending"
                          }`}
                        >
                          {d.openCount > 0 ? "Viewed By Client" : "Not Viewed"}
                        </div>

                        {d.openCount > 0 && (
                          <div className="doc-live-pulse">
                            <span></span>
                            Active
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="doc-empty-state">
                  <div className="doc-empty-icon">
                    <i className="ti ti-files-off"></i>
                  </div>

                  <div className="doc-empty-title">No Documents Yet</div>

                  <div className="doc-empty-subtitle">
                    Generated documents will appear here
                  </div>
                </div>
              )}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="journey-card mb-4"
            >
              {/* HEADER */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h4 className="journey-title mb-1">
                    🚀 Client Journey Tracker
                  </h4>

                  <small className="journey-subtitle">
                    {" "}
                    Realtime client activity tracking
                  </small>
                </div>

                <motion.div
                  animate={{
                    scale: [1, 1.08, 1],
                  }}
                  transition={{
                    duration: 1.8,
                    repeat: Infinity,
                  }}
                  className="journey-progress-badge"
                >
                  {journey?.progress || 0}%
                </motion.div>
              </div>

              {/* PROGRESS BAR */}
              <div className="journey-progress-wrapper">
                <motion.div
                  className="journey-progress-bar"
                  initial={{ width: 0 }}
                  animate={{
                    width: `${journey?.progress || 0}%`,
                  }}
                  transition={{
                    duration: 1,
                  }}
                />
              </div>

              {/* CURRENT STEP */}
              <motion.div
                whileHover={{ scale: 1.01 }}
                className="current-step-box mt-4"
              >
                <div className="step-label">Current Step</div>

                <div className="step-value">
                  {journey?.currentStep?.replaceAll("_", " ")?.toUpperCase() ||
                    "NO ACTIVITY"}
                </div>
              </motion.div>

              {/* LAST ACTIVE */}
              <div className="last-active-box">
                <div className="text-muted mb-1">Last Activity</div>

                <div className="fw-bold">
                  {journey?.lastActiveAt
                    ? new Date(journey.lastActiveAt).toLocaleString()
                    : "-"}
                </div>
              </div>

              {/* TIMELINE */}
              <div className="timeline-wrapper mt-5">
                <div className="timeline-title">Journey Timeline</div>

                {journey?.timeline
                  ?.slice()
                  ?.reverse()
                  ?.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: index * 0.08,
                      }}
                      whileHover={{
                        x: 6,
                      }}
                      className="timeline-item"
                    >
                      {/* DOT + LINE */}
                      <div className="timeline-left">
                        <div className="timeline-dot" />

                        {index !== journey.timeline.length - 1 && (
                          <div className="timeline-line" />
                        )}
                      </div>

                      {/* CONTENT */}
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <div className="timeline-step">{item.title}</div>

                          <div className="timeline-time">
                            {new Date(item.createdAt).toLocaleString()}
                          </div>
                        </div>

                        <div className="timeline-description">
                          {item.description}
                        </div>
                      </div>
                    </motion.div>
                  ))}
              </div>
            </motion.div>
          </div>

          {/* ================= RIGHT COLUMN ================= */}
          <div className="col-md-8">
            <div className="card mb-3 border-0 shadow-sm">
              <div className="card-body">
                <h5 className="mb-3">Lead Pipeline Status</h5>
                <ul className="pipeline-list d-flex gap-2 flex-wrap list-unstyled">
                  {followUp.map((f, i) => {
                    const color =
                      DISPOSITION_COLORS[f.disposition] || "bg-secondary";
                    return (
                      <li key={i}>
                        <span className={`badge ${color} p-2 px-3`}>
                          {f.disposition}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body">
                <h5>Meetings</h5>

                {meetings.map((m) => (
                  <div key={m._id}>
                    <p>{m.title}</p>
                    <p>{new Date(m.startTime).toLocaleString()}</p>

                    <a href={m.meetLink} target="_blank" rel="noreferrer">
                      Join Meeting
                    </a>
                  </div>
                ))}
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent border-bottom d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Followup History</h5>
                {/* ✅ Permission check for FOLLOWUP module */}
                {can(permissions, "write", "followup") && (
                  <button
                    className="btn btn-danger btn-sm"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#followupModal"
                    onClick={() => setFollowupsData(lead)}
                  >
                    <i className="ti ti-plus me-1"></i> Add Followup
                  </button>
                )}
              </div>

              <div className="card-body">
                {followUp.length ? (
                  followUp.map((item, i) => (
                    <div className="card border shadow-none mb-3" key={i}>
                      <div className="card-body">
                        <div
                          className="card border-0 mb-3"
                          style={{
                            borderRadius: "12px",
                            boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                            transition: "0.3s",
                          }}
                        >
                          <div className="card-body">
                            {/* HEADER */}
                            <div className="d-flex justify-content-between">
                              <h6 className="fw-bold text-danger">
                                {item.disposition}

                                {item.sub_disposition && (
                                  <span className="text-dark">
                                    {" "}
                                    → {item.sub_disposition}
                                  </span>
                                )}
                              </h6>

                              <small className="text-muted">
                                {new Date(item.createdAt).toLocaleString()}
                              </small>
                            </div>

                            {item.disposition === "Payment" && (
                              <div
                                className="mt-3 p-3 rounded"
                                style={{
                                  background: "#f8fafc",
                                  border: "1px solid #e2e8f0",
                                }}
                              >
                                <h6 className="fw-bold text-success mb-3">
                                  💳 Payment Details
                                </h6>

                                <div className="row">
                                  {/* AMOUNT */}
                                  <div className="col-md-6 mb-2">
                                    <small className="text-muted">Amount</small>

                                    <div className="fw-semibold text-dark">
                                      ₹ {item.payment_amount || "-"}
                                    </div>
                                  </div>

                                  {/* TRANSACTION ID */}
                                  <div className="col-md-6 mb-2">
                                    <small className="text-muted">
                                      Transaction ID
                                    </small>

                                    <div className="fw-semibold text-dark">
                                      {item.transaction_id || "-"}
                                    </div>
                                  </div>

                                  {/* MODE */}
                                  <div className="col-md-6 mb-2">
                                    <small className="text-muted">
                                      Payment Mode
                                    </small>

                                    <div className="fw-semibold text-dark">
                                      {item.payment_mode || "-"}
                                    </div>
                                  </div>

                                  {/* ORDER ID */}
                                  <div className="col-md-6 mb-2">
                                    <small className="text-muted">
                                      Order ID
                                    </small>

                                    <div className="fw-semibold text-dark">
                                      {item.order_id || "-"}
                                    </div>
                                  </div>

                                  {/* DATE */}
                                  <div className="col-md-12 mt-2">
                                    <small className="text-muted">
                                      Payment Date & Time
                                    </small>

                                    <div className="fw-semibold text-dark">
                                      {item.payment_date
                                        ? new Date(
                                            item.payment_date,
                                          ).toLocaleString()
                                        : "-"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* REMARKS */}
                            {item.remarks && (
                              <div
                                className="p-3 mt-3 rounded"
                                style={{
                                  background: "#f8fafc",
                                  border: "1px solid #e2e8f0",
                                }}
                              >
                                <div className="fw-bold mb-1">📝 Remarks</div>

                                <div>{item.remarks}</div>
                              </div>
                            )}

                            {/* PAYMENT DETAILS */}

                            {/* DYNAMIC FIELDS */}
                            {item.fill_details &&
                              Object.keys(item.fill_details).length > 0 && (
                                <div
                                  className="mt-3 p-3 rounded"
                                  style={{
                                    background: "#fff",
                                    border: "1px solid #e5e7eb",
                                  }}
                                >
                                  <h6 className="fw-bold mb-3">
                                    📌 Followup Details
                                  </h6>

                                  {Object.entries(item.fill_details).map(
                                    ([key, value]) => (
                                      <div
                                        key={key}
                                        className="d-flex justify-content-between border-bottom py-2"
                                      >
                                        <span className="fw-semibold">
                                          {key.replace(/_/g, " ")}
                                        </span>

                                        <span>{value || "-"}</span>
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}

                            {/* PRICE (0 FIXED) */}
                            {/* {item.price > 0 && (
                              <div className="mt-2 text-success fw-bold">
                                💰 ₹{item.price}
                              </div>
                            )} */}

                            {/* DOCUMENT SENT DETAILS */}

                            {item.sub_disposition?.toLowerCase() ===
                              "document sent to the customer" && (
                              <div
                                className="mt-3 p-3 rounded"
                                style={{
                                  background: "#f8fafc",
                                  border: "1px solid #e2e8f0",
                                }}
                              >
                                <h6
                                  className="fw-bold mb-3"
                                  style={{
                                    color: "#dc2626",
                                  }}
                                >
                                  📄 Document Details
                                </h6>

                                {/* PACKAGE */}

                                {item.packageId?.packageName && (
                                  <div className="mb-2">
                                    📦 <b>Selected Package:</b>{" "}
                                    {item.packageId.packageName}
                                  </div>
                                )}

                                {/* PRICE */}

                                {Number(item.price) > 0 && (
                                  <div
                                    className="fw-bold"
                                    style={{
                                      color: "#16a34a",
                                    }}
                                  >
                                    💰 Price: ₹{item.price}
                                  </div>
                                )}
                              </div>
                            )}
                            {/* PRICE */}
                            <div className="mt-3">
                              {/* PRICE */}

                              {/* PENDING */}
                              {/* PENDING */}

                              {item.disposition === "Converted" &&
                                item.sub_disposition === "Onboarded" && (
                                  <button
                                    className="btn btn-primary mt-3"
                                    onClick={() =>
                                      navigate(`/create-project/${lead._id}`)
                                    }
                                  >
                                    🚀 Create Project
                                  </button>
                                )}
                              {item.disposition ===
                                "Website Completed & Delivered" &&
                                Number(item.pendingAmount) > 0 && (
                                  <>
                                    <div
                                      className="fw-bold mt-2"
                                      style={{
                                        color: "#ea580c",
                                      }}
                                    >
                                      🪙 Pending Amount: ₹{item.pendingAmount}
                                    </div>

                                    <div
                                      className="fw-bold mt-2"
                                      style={{
                                        color: "#dc2626",
                                      }}
                                    >
                                      💰 Total Amount: ₹
                                      {Number(item.pendingAmount || 0) +
                                        (item.additionalPayments || []).reduce(
                                          (acc, pay) =>
                                            acc + Number(pay.amount || 0),
                                          0,
                                        )}
                                    </div>
                                  </>
                                )}

                              {/* TOTAL */}
                              {Number(item.totalAmount) > 0 && (
                                <div
                                  className="fw-bold mt-2"
                                  style={{
                                    color: "#dc2626",
                                    fontSize: "16px",
                                  }}
                                >
                                  💳 Total Amount: ₹{item.totalAmount}
                                </div>
                              )}

                              {/* WEBSITE URL */}
                              {item?.fill_details?.websiteUrl && (
                                <div
                                  className="mt-2"
                                  style={{
                                    wordBreak: "break-word",
                                  }}
                                >
                                  🌐 Website:
                                  <a
                                    href={item.fill_details.websiteUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="ms-1"
                                  >
                                    {item.fill_details.websiteUrl}
                                  </a>
                                </div>
                              )}

                              {/* ADDITIONAL PAYMENTS */}
                              {item?.additionalPayments?.length > 0 && (
                                <div className="mt-3">
                                  <div
                                    className="fw-bold mb-2"
                                    style={{
                                      color: "#dc2626",
                                    }}
                                  >
                                    ➕ Additional Charges
                                  </div>

                                  {item.additionalPayments.map((pay, index) => (
                                    <div
                                      key={index}
                                      className="d-flex justify-content-between align-items-center mb-2 p-2 rounded"
                                      style={{
                                        background: "#fff7ed",
                                        border: "1px solid #fed7aa",
                                      }}
                                    >
                                      <span>{pay.reason}</span>

                                      <strong>₹{pay.amount}</strong>
                                    </div>
                                  ))}

                                  <div
                                    style={{
                                      marginTop: "10px",
                                      fontWeight: "700",
                                      color: "#dc2626",
                                    }}
                                  >
                                    💰 Total Amount: ₹
                                    {Number(item.pendingAmount || 0) +
                                      Number(
                                        (item.additionalPayments || []).reduce(
                                          (acc, pay) =>
                                            acc + Number(pay.amount || 0),
                                          0,
                                        ),
                                      )}
                                  </div>
                                </div>
                              )}

                              {/* ADD PRICE BUTTON */}
                              {/* {item.sub_disposition?.toLowerCase() ===
                                "document sent to the customer" && (
                                <button
                                  className="btn btn-sm btn-outline-primary mt-3"
                                  onClick={() => openPriceModal(item)}
                                >
                                  ✏️ Add Price
                                </button>
                              )} */}
                            </div>

                            {/* COMMITMENTS */}
                            {/* {item.commitments?.length > 0 && (
                              <div className="mt-3">
                                <b>📦 Commitments:</b>

                                {item.commitments.map((c, i) => (
                                  <div
                                    key={i}
                                    className="mt-2 p-2 rounded"
                                    style={{ background: "#f8fafc" }}
                                  >
                                    <div>
                                      <b>{c.moduleName || "-"}</b>
                                    </div>
                                    <div className="text-muted">
                                      {c.description || "-"}
                                    </div>

                                    {c.price > 0 && (
                                      <div className="text-success">
                                        ₹{c.price}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )} */}

                            {(Number(item.price || 0) > 0 ||
                              item.packageId ||
                              item.commitments?.length > 0) && (
                              <div
                                className="mt-3 p-3 rounded"
                                style={{
                                  background: "#f8fafc",
                                  border: "1px solid #e2e8f0",
                                }}
                              >
                                <h6
                                  className="fw-bold mb-3"
                                  style={{
                                    color: "#dc2626",
                                  }}
                                >
                                  📄 Proposal / Add-ons Details
                                </h6>

                                {/* PACKAGE */}

                                {item.packageId?.packageName && (
                                  <div className="mb-2">
                                    📦 <b>Package:</b>{" "}
                                    {item.packageId.packageName}
                                  </div>
                                )}

                                {/* MAIN PRICE */}
                                {/* 
                                {Number(item.price) > 0 && (
                                  <div className="mb-2 text-success fw-bold">
                                    💰 Proposal Price: ₹
                                    {Number(item.price).toLocaleString()}
                                  </div>
                                )} */}

                                {/* COMMITMENTS */}

                                {item.commitments?.length > 0 && (
                                  <>
                                    <div className="fw-bold mb-2">
                                      📦 Additional Commitments
                                    </div>

                                    {item.commitments.map((c, i) => (
                                      <div
                                        key={i}
                                        className="p-2 mb-2 rounded"
                                        style={{
                                          background: "#fff",
                                          border: "1px solid #e5e7eb",
                                        }}
                                      >
                                        <div>
                                          <b>{c.moduleName}</b>
                                        </div>

                                        <div className="text-muted">
                                          {c.description}
                                        </div>

                                        <div className="text-success fw-bold">
                                          ₹
                                          {Number(
                                            c.price || 0,
                                          ).toLocaleString()}
                                        </div>
                                      </div>
                                    ))}
                                  </>
                                )}

                                {/* TOTAL */}

                                <div
                                  className="mt-3 pt-2"
                                  style={{
                                    borderTop: "1px dashed #cbd5e1",
                                  }}
                                >
                                  <div
                                    className="fw-bold"
                                    style={{
                                      color: "#dc2626",
                                      fontSize: "16px",
                                    }}
                                  >
                                    Total Price: ₹
                                    {(
                                      Number(item.price || 0) +
                                      (item.commitments || []).reduce(
                                        (acc, c) => acc + Number(c.price || 0),
                                        0,
                                      )
                                    ).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* USER */}
                            <div className="mt-3">
                              <small className="badge bg-light text-dark">
                                👤 {ucfirst(item.userId?.name || "System")}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <i className="ti ti-notes-off fs-40 text-muted mb-2"></i>
                    <p className="text-muted">No follow-ups recorded yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* PRICE UPDATE MODAL */}

        <div className="modal fade" id="priceModal" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0">
              <div className="modal-header">
                <h5 className="modal-title">Update Pricing</h5>

                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                />
              </div>

              <div className="modal-body">
                <label className="mb-2 fw-semibold">Enter Price</label>

                <input
                  type="number"
                  className="form-control"
                  placeholder="Enter amount"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                />
              </div>

              <div className="modal-footer">
                <button className="btn btn-primary" onClick={handleUpdatePrice}>
                  Save Price
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ================= OFFCANVAS ================= */}
        <div
          className="offcanvas offcanvas-end offcanvas-large"
          id="followupModal"
          style={{ overflow: "scroll" }}
        >
          {followupsData && (
            <Followup data={followupsData} onSuccess={fetchFollowups} />
          )}
        </div>

        <div
          className="offcanvas offcanvas-end offcanvas-large"
          id="offcanvas_add"
        >
          {sendemailData && <AddDetails data={sendemailData} />}
        </div>
      </div>

      <div className="modal fade" id="projectModal" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content border-0">
            <div className="modal-header">
              <h5 className="modal-title">🚀 Create Project</h5>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>

            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Project Name</label>

                <input
                  type="text"
                  className="form-control"
                  value={projectForm.projectName}
                  onChange={(e) =>
                    setProjectForm({
                      ...projectForm,
                      projectName: e.target.value,
                    })
                  }
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Assign Developer</label>

                <select
                  className="form-select"
                  value={projectForm.assignedDeveloper}
                  onChange={(e) =>
                    setProjectForm({
                      ...projectForm,
                      assignedDeveloper: e.target.value,
                    })
                  }
                >
                  <option value="">Select Developer</option>

                  {developers.map((d) => (
                    <option key={d._id} value={d._id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Priority</label>

                <select
                  className="form-select"
                  value={projectForm.priority}
                  onChange={(e) =>
                    setProjectForm({
                      ...projectForm,
                      priority: e.target.value,
                    })
                  }
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Deadline</label>

                <input
                  type="date"
                  className="form-control"
                  value={projectForm.deadline}
                  onChange={(e) =>
                    setProjectForm({
                      ...projectForm,
                      deadline: e.target.value,
                    })
                  }
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Internal Notes</label>

                <textarea
                  rows="4"
                  className="form-control"
                  value={projectForm.notes}
                  onChange={(e) =>
                    setProjectForm({
                      ...projectForm,
                      notes: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleCreateProject}>
                Create Project
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LeadDetails;
  